import cx from 'classnames';
import { useAtom } from 'jotai';
import useTranslation from 'next-translate/useTranslation';
import React, { FunctionComponent, ReactNode, useEffect } from 'react';
import Modal from 'react-modal';

import useIsMobile from '../../../../hooks/use-is-mobile';
import Button from '../../button';
import IconClose from '../../icons/close';
import IconGallery from '../../icons/gallery';
import BPImage from '../../image';
import NoImage, { TNoImage } from '../../no-image-placeholder';

import ImageGallery from './image-gallery';
import classes from './image-group.module.css';
import ImageSlider from './image-slider';
import { isSliderOpen, targetItem } from './state';
import Availability from '../../availability';

Modal.setAppElement('#__next');

const getNodeText = (node: ReactNode): string => {
  if (['string', 'number'].includes(typeof node)) return node as string;
  if (node instanceof Array) return node.map(getNodeText).join('');
  if (typeof node === 'object' && node) return getNodeText(node.props.children);
  return '';
};

const ImageGroup: FunctionComponent<{
  list: TProductDetail['images'];
  modalTitle?: ReactNode;
  qtt?: number;
  altExtraInfo?: string;
  noImageVariant?: TNoImage['variant'];
  noImageColor?: TNoImage['color'];
  border?: boolean;
  expressShipping?: boolean;
  steering_wheel_side?: 'R' | 'L' | null;
  status?: string;
  ref?: any;
  isPP?: boolean;
}> = ({
  list: originalList,
  modalTitle,
  qtt = 4,
  altExtraInfo,
  noImageVariant = 'gray',
  noImageColor = 'text-neutral-50',
  border,
  expressShipping,
  steering_wheel_side,
  status,
  ref,
  isPP,
}) => {
    const { t } = useTranslation();
    const [open, setOpen] = useAtom(isSliderOpen);
    const [index, setIndex] = useAtom(targetItem);
    const isMobile = useIsMobile();
    const isUkWebsite = process.env.NEXT_PUBLIC_HOST?.includes('.co.uk');

    useEffect(() => {
      setOpen(false);
    }, []);

    const list = originalList.filter(
      ({ image, image_big, image_thumb }) => image && image_big && image_thumb
    );

    if (list.length === 0) {
      return (
        <div
          className={cx(isPP ? 'w-full h-fit aspect-video' : 'h-[219px] lg:h-36')}
          ref={ref}
        >
          <NoImage
            variant={noImageVariant}
            color={noImageColor}
            label={t('unique-vehicle:no_image')}
          />
        </div>
      );
    }

    const slides: { src: string; thumbnail: string; src_big: string }[] = [];
    list.forEach((element, index) => {
      slides.push({
        src: element.image,
        src_big: element.image_big,
        thumbnail: element.image_big,
      });
    });

    return (
      <div ref={ref}>
        <ul
          className={cx(
            { [classes.mainDisplay]: qtt > 1 },
            'grid',
            'grid-flow-col-dense',
            'gap-4'
          )}
        >
          <li
            className={cx(
              'relative',
              'col-span-3',
              'cursor-pointer',
              border
                ? 'p-2 bg-white shadow-[0_0_8px_0_rgba(0,0,0,0.1)]'
                : 'shadow-light',
              'w-full h-fit aspect-video'
            )}
          >
            <>
              <ImageSlider
                list={slides}
                isPP={isPP}
                cardView={'PP'}
                altExtraInfo={modalTitle ? `${getNodeText(modalTitle)}` : ''}
              />
              <div
                className={cx(
                  'absolute py-[2px] px-1 bg-blue-100 text-sm font-bold text-white rounded-sm flex gap-1 items-center justify-center',
                  border ? ' bottom-4 left-4 ' : ' bottom-2 left-2 '
                )}
              >
                <IconGallery className="w-3 h-3"></IconGallery>
                {slides.length}
              </div>
              {expressShipping && status === 'STO' ? (
                <div
                  className={cx(
                    'absolute p-1 bg-cta-100 text-sm font-bold text-white rounded-sm',
                    border ? 'top-4 left-4' : 'top-2 left-2'
                  )}
                >
                  {t('product:expressShipping')}
                </div>
              ) : (
                <>{status && <div
                  className={cx(
                    'absolute p-1 text-sm font-bold  rounded-sm',
                    status === 'RSV' ? 'text-neutral-80' : 'text-white',
                    border ? 'top-4 left-4' : 'top-2 left-2'
                  )}
                >
                  <Availability status={status} />
                </div>}</>
              )}
              {((steering_wheel_side && steering_wheel_side === 'R') ||
                isUkWebsite) && (
                  <div
                    className={cx(
                      'absolute p-1 bg-neutral-80 text-sm font-bold text-white rounded-sm',
                      border ? 'top-4 right-4' : 'top-2 right-2'
                    )}
                  >
                    {steering_wheel_side && steering_wheel_side === 'R'
                      ? t('product:wheelSide.R')
                      : t('product:wheelSide.L')}
                  </div>
                )}
            </>
          </li>
          {isPP && list.slice(1, qtt).map((el, index) => (
            <li
              key={`galImage-${el.image}`}
              className={cx(
                'hidden',
                'lg:block',
                border
                  ? 'p-2 bg-white shadow-[0_0_8px_0_rgba(0,0,0,0.1)]'
                  : 'shadow-light'
              )}
              onClick={() => {
                setIndex(index + 1), setOpen(true);
              }}
            >
              <div
                className={cx(
                  classes.imageRatioSmall,
                  'relative',
                  'hidden',
                  'lg:block',
                  'cursor-pointer',
                  classes.ieSpace
                )}
              >
                <BPImage
                  key={el.image_big}
                  src={el.image_big}
                  layout="fill"
                  objectFit="cover"
                  alt={modalTitle ? `${getNodeText(modalTitle)}` : ''}
                  priority
                />
              </div>
            </li>
          ))}
        </ul>
        <Modal
          onAfterOpen={() => {
            document.body.style.overflow = 'hidden';
          }}
          onAfterClose={() => {
            document.body.style.overflow = 'auto';
            if (index > 2) {
              setIndex(0);
            }
          }}
          isOpen={open}
          onRequestClose={() => setOpen(false)}
          overlayClassName={cx(
            'top-0',
            'bottom-0',
            'left-0',
            'right-0',
            'fixed',
            'z-50'
          )}
          className={cx(
            'top-0',
            'left-0',
            'right-0',
            'bottom-0',
            'bg-neutral-80',
            'fixed',
            'z-50'
          )}
          contentLabel="Images"
        >
          <div className="w-full bg-white pl-4 z-[1000] relative flex flex-row items-center justify-between">
            {modalTitle && <div className="truncate">{modalTitle}</div>}
            <Button
              isFullWidth={false}
              onClick={() => setOpen(false)}
              aria-label="close modal"
              className={cx('p-4', 'justify-self-end')}
            >
              <IconClose className={cx('h-4', 'text-neutral-80')} />
            </Button>
          </div>
          <div className="flex flex-col lg:flex-row h-[calc(100%-48px)] w-full lg:gap-4 justify-between">
            <ImageGallery
              list={slides}
              altExtraInfo={modalTitle ? `${getNodeText(modalTitle)}` : ''}
            />
          </div>
        </Modal>
      </div>
    );
  };

export default ImageGroup;
